import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { getTargetValue } from "../../../shared/connectors/getTargetValue";
import {
  applyNormalize,
  parseUrlInput,
  computeSegments,
  type Segment,
} from "../../../shared/connectors/normalizeInput";
import type {
  HostnameField,
  InputField,
  SelectField,
  UrlField,
} from "../../../shared/connectors/types";
import { sharedMutators } from "../../../shared/mutators";
import { trySealSecret } from "../../sealSecret";
import { Box } from "../Box";
import { Dropdown } from "../Dropdown";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { DropdownOpener } from "../Settings/shared";
import { Text } from "../Text";
import type { App } from "./Form";
import { useRenderWithPlaceholders } from "./renderWithPlaceholders";

const InputContainer = styled("div")`
  display: flex;
  flex-direction: column;
`;

const NormalizedInputWrapper = styled("div")`
  position: relative;
`;

const SegmentOverlay = styled("div")`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  padding: 8px 12px;
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;
  white-space: pre;
  overflow: hidden;
`;

type Props = {
  item: InputField | UrlField | HostnameField | SelectField;
  app: App;
  locallySet?: ReadonlySet<string>;
  onFieldSet?: (key: string) => void;
  disabled?: boolean;
  onEdit?: () => void;
};

const normalizeForKind = (kind: Props["item"]["kind"]) => {
  if (kind === "url") return (url: URL) => url;
  if (kind === "hostname") return (url: URL) => url.hostname;
  return undefined;
};

export function FieldItem({
  item,
  app,
  locallySet,
  onFieldSet,
  disabled,
  onEdit,
}: Props) {
  const z = useZero();
  const renderWithPlaceholders = useRenderWithPlaceholders();
  const value = getTargetValue(item.target, app);
  const { target } = item;
  const fieldKey = `${target.kind}:${target.field}`;
  const isMasked = target.kind === "secrets" || target.field === "clientSecret";
  // Secret values never replicate to the client; presence comes from the
  // non-secret `secretKeys`/`hasOAuthClientSecret` companions the server maintains,
  // plus this session's own edits before they round-trip.
  const secretIsSet =
    (locallySet?.has(fieldKey) ?? false) ||
    (target.kind === "secrets"
      ? (app.secretKeys?.includes(target.field) ?? false)
      : target.field === "clientSecret"
        ? (app.hasOAuthClientSecret ?? false)
        : false);
  const normalizeFn = normalizeForKind(item.kind);
  const [segments, setSegments] = useState<Segment[] | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // The input is uncontrolled (defaultValue) so a sibling can edit the same
  // field — e.g. the workflow picker renaming the app — without us losing
  // typed-in changes. Sync from `value` only when the input isn't focused.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (el.value !== (value ?? "")) {
      el.value = value ?? "";
    }
  }, [value]);

  const setValue = async (rawValue: string) => {
    if (normalizeFn && rawValue.trim() && !parseUrlInput(rawValue)) return;
    const newValue = normalizeFn
      ? applyNormalize(rawValue, normalizeFn)
      : rawValue;
    if ((isMasked || item.required) && !newValue) return;
    let secretSeal: { field: string; value: string } | undefined;
    if (target.kind === "secrets") {
      const value = await trySealSecret(newValue);
      if (value === undefined) return;
      secretSeal = { field: target.field, value };
    }
    if (isMasked && newValue) onFieldSet?.(fieldKey);
    if (target.kind === "oauthConnection") {
      if (!app.oauthConnectionId) return;
      return z.mutate(
        sharedMutators.updateOAuthConnection({
          oauthConnectionId: app.oauthConnectionId,
          [target.field]:
            target.field === "scopes"
              ? newValue.split(/\s+/).filter((s) => s.length > 0)
              : newValue,
        })
      );
    }

    const base = {
      connectedAppId: app.id,
      name: app.name,
      identifier: app.identifier,
      urlPattern: app.urlPattern,
      configuration: app.configuration,
    };

    if (target.kind === "connectedApp") {
      return z.mutate(
        sharedMutators.updateConnectedApp({
          ...base,
          [target.field]: newValue,
          name: target.field === "name" ? newValue : app.name,
          urlPattern: target.field === "urlPattern" ? newValue : app.urlPattern,
        })
      );
    }

    if (secretSeal) {
      return z.mutate(
        sharedMutators.updateConnectedApp({
          ...base,
          secrets: { [secretSeal.field]: secretSeal.value },
        })
      );
    }

    const updated = { ...app.configuration, [target.field]: newValue };
    z.mutate(
      sharedMutators.updateConnectedApp({
        ...base,
        configuration: updated,
      })
    );
  };

  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md" color="var(--text-primary)">
          {item.label}
        </Text>
      </Box>
      <InputContainer>
        {item.kind === "select" ? (
          <Dropdown
            opener={
              <DropdownOpener type="button" disabled={disabled}>
                <Text variant="text-regular">
                  {item.options.find(
                    (o) => o.value === (value || item.defaultValue)
                  )?.label ?? item.options[0]?.label}
                </Text>
                <span style={{ opacity: 0.3 }}>
                  <Icon name="chevron-down" size={12} />
                </span>
              </DropdownOpener>
            }
            align="bottom-left"
            sections={[
              {
                items: item.options.map((option) => ({
                  type: "button" as const,
                  left: null,
                  label: option.label,
                  right:
                    option.value === (value || item.defaultValue) ? (
                      <Icon
                        name="check"
                        size={12}
                        color="var(--text-secondary)"
                      />
                    ) : undefined,
                  onClick: () => {
                    onEdit?.();
                    setValue(option.value);
                  },
                })),
              },
            ]}
          />
        ) : (
          <NormalizedInputWrapper>
            {segments && (
              <SegmentOverlay>
                {segments.map((seg) => (
                  <span
                    key={`${seg.kept}-${seg.text}`}
                    style={{ opacity: seg.kept ? 1 : 0.3 }}
                  >
                    {seg.text}
                  </span>
                ))}
              </SegmentOverlay>
            )}
            <Input
              ref={inputRef}
              type="text"
              // A set secret shows as placeholder dots, not a value, so don't
              // let `required` flag a set masked field as empty.
              required={item.required && !(isMasked && secretIsSet)}
              secret={isMasked}
              secretIsSet={secretIsSet}
              defaultValue={
                value ||
                (item.defaultValue
                  ? renderWithPlaceholders(item.defaultValue)
                  : undefined)
              }
              placeholder={item.placeholder}
              style={{
                width: "100%",
                ...(segments
                  ? { color: "transparent", caretColor: "var(--text-primary)" }
                  : {}),
              }}
              onChange={(e) => {
                if (normalizeFn) {
                  const raw = e.target.value;
                  e.target.setCustomValidity(
                    !raw.trim() || parseUrlInput(raw) ? "" : "Enter a valid URL"
                  );
                  const normalized = applyNormalize(raw, normalizeFn);
                  if (normalized !== raw) {
                    setSegments(computeSegments(raw, normalizeFn));
                  } else {
                    setSegments(null);
                  }
                }
              }}
              onBlur={(e) => {
                if (item.required && !e.target.value && value) {
                  e.target.value = value;
                }
              }}
              onFinishChange={setValue}
              readOnly={disabled}
            />
          </NormalizedInputWrapper>
        )}
      </InputContainer>
      {item.helperText && (
        <Box horizontal={8} top={6}>
          <Text variant="text-small" color="var(--text-secondary)">
            {renderWithPlaceholders(item.helperText)}
          </Text>
        </Box>
      )}
    </div>
  );
}
